import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useNotificationSound } from '../../utils/notificationSound';

const EMOJI_LIST = ['👍','❤️','😂','😮','😢','👏'];
const authH = token => ({ headers: { Authorization: `Bearer ${token}` } });
const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : '';
const initials = name => name ? name.split(' ').map(w=>w[0]).join('').toUpperCase().substring(0,2) : '?';
const sortConvs = list => [...list].sort((a,b) => {
  // Unread conversations always float to top
  const aUnread = (a.adminUnread || 0) > 0;
  const bUnread = (b.adminUnread || 0) > 0;
  if (aUnread && !bUnread) return -1;
  if (!aUnread && bUnread) return 1;
  // Then sort by most recent message
  if (a.lastMessageAt && b.lastMessageAt) return b.lastMessageAt.localeCompare(a.lastMessageAt);
  if (a.lastMessageAt) return -1; if (b.lastMessageAt) return 1;
  return (a.agentName||'').localeCompare(b.agentName||'');
});

function fileIcon(mt) {
  if (!mt) return '📎';
  if (mt.startsWith('image/')) return '🖼️';
  if (mt.startsWith('video/')) return '🎬';
  if (mt.startsWith('audio/')) return '🎵';
  if (mt.includes('pdf')) return '📄';
  return '📎';
}
function humanSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b/1024).toFixed(1) + ' KB';
  return (b/1048576).toFixed(1) + ' MB';
}

function MsgBubble({ msg, myEmail, onDelete, onReact }) {
  const isMe = msg.senderEmail === myEmail || msg.senderRole === 'admin';
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);

  useEffect(() => {
    if (!showEmoji) return;
    const h = (e) => { if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showEmoji]);

  if (msg.deletedAt) return (
    <div style={{textAlign:isMe?'right':'left',padding:'4px 16px'}}>
      <span style={{fontSize:12,color:'#9ca3af',fontStyle:'italic'}}>Message deleted</span>
    </div>
  );

  const bubbleBg = isMe ? '#1a56db' : '#fff';
  const bubbleColor = isMe ? '#fff' : '#111827';
  const radius = isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px';

  const renderContent = () => {
    if (msg.fileUrl) {
      if (msg.type === 'image') return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
          <img src={msg.fileUrl} alt={msg.fileName||'Image'} style={{maxWidth:220,borderRadius:8,display:'block'}}
            onError={e=>{e.target.style.display='none';}} />
          {msg.content && <div style={{fontSize:12,marginTop:4,opacity:.85}}>{msg.content}</div>}
        </a>
      );
      return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer"
          style={{display:'flex',alignItems:'center',gap:8,color:isMe?'#fff':'#1a56db',textDecoration:'none'}}>
          <span style={{fontSize:22}}>{fileIcon(msg.mimeType)}</span>
          <div>
            <div style={{fontWeight:600,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{msg.fileName||'File'}</div>
            <div style={{fontSize:11,opacity:.7}}>{humanSize(msg.fileSize)}</div>
          </div>
        </a>
      );
    }
    return <span style={{wordBreak:'break-word'}}>{msg.content}</span>;
  };

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:isMe?'flex-end':'flex-start',marginBottom:8,padding:'0 12px'}}>
      <div style={{maxWidth:'70%',position:'relative'}}>
        <div onClick={()=>setShowEmoji(v=>!v)}
          style={{background:bubbleBg,color:bubbleColor,padding:'10px 14px',borderRadius:radius,fontSize:14,boxShadow:'0 1px 2px rgba(0,0,0,.08)',cursor:'pointer'}}>
          {renderContent()}
        </div>
        <div style={{fontSize:10,color:'#9ca3af',marginTop:2,textAlign:isMe?'right':'left'}}>{fmtTime(msg.timestamp)}</div>
        {msg.reactions && Object.keys(msg.reactions).length>0 && (
          <div style={{display:'flex',gap:4,marginTop:4,flexWrap:'wrap'}}>
            {Object.entries(msg.reactions).map(([e,u])=>(
              <span key={e} onClick={()=>onReact(msg.messageId,e)} style={{cursor:'pointer',background:'#f3f4f6',borderRadius:12,padding:'2px 7px',fontSize:13,border:'1px solid #e5e7eb'}}>{e} {u.length}</span>
            ))}
          </div>
        )}
        {showEmoji && (
          <div ref={emojiRef} style={{position:'absolute',top:-46,right:isMe?0:'auto',left:isMe?'auto':0,background:'#fff',border:'1px solid #e5e7eb',borderRadius:20,padding:'6px 10px',display:'flex',gap:6,boxShadow:'0 4px 16px rgba(0,0,0,.15)',zIndex:20,alignItems:'center'}}>
            {EMOJI_LIST.map(e=>(
              <span key={e} onClick={ev=>{ev.stopPropagation();onReact(msg.messageId,e);setShowEmoji(false);}}
                style={{cursor:'pointer',fontSize:18,padding:'2px',lineHeight:1}}>{e}</span>
            ))}
            {isMe && <span onClick={ev=>{ev.stopPropagation();onDelete(msg.messageId);setShowEmoji(false);}}
              style={{cursor:'pointer',fontSize:15,padding:'2px 4px',color:'#ef4444',marginLeft:4,borderLeft:'1px solid #e5e7eb',paddingLeft:8}}>🗑</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminSupport({ token }) {
  const [convs, setConvs]       = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [sending, setSending]   = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const bottomRef   = useRef(null);
  const socketRef   = useRef(null);
  const selectedRef = useRef(null);
  const fileRef     = useRef(null);
  const playSound   = useNotificationSound();
  const myEmail = (() => { try { return JSON.parse(atob((token||'').split('.')[1])).email||'admin'; } catch { return 'admin'; } })();
  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // Load ALL agents merged with existing conversations
  const loadConvs = useCallback(async () => {
    try {
      const cid = localStorage.getItem('selectedCampaign')||'medicare';
      const [cr,ar] = await Promise.all([
        axios.get('/api/support/admin/conversations', authH(token)),
        axios.get(`/api/admin/agents?campaignId=${cid}`, authH(token))
      ]);
      const existing = cr.data.conversations||[];
      // Build case-insensitive map so "Mligon@insurer.com" matches "mligon@insurer.com"
      const em = new Map(existing.map(c=>[(c.agentEmail||'').toLowerCase(),c]));
      const rows = [];
      const buildName = (obj, fallback) => {
        const first = (obj.firstName||'').trim();
        const last  = (obj.lastName||'').trim();
        if (first || last) return [first,last].filter(Boolean).join(' ');
        return (obj.name||'').trim() || fallback;
      };
      for (const a of (ar.data||[])) {
        if (a.agentType==='group') {
          for (const m of (a.groupMembers||[])) {
            if (!m.email) continue;
            const e=m.email.toLowerCase();
            const name=buildName(m, e);
            // Always use the real name from the agents API, even if a conversation already exists
            const existing = em.get(e);
            rows.push(existing ? {...existing, agentName:name} : {conversationId:null,agentEmail:e,agentName:name,lastMessage:'',lastMessageAt:'',adminUnread:0,agentUnread:0,_noConv:true});
          }
        } else {
          const e=(a.email||'').toLowerCase(); if(!e) continue;
          const name=buildName(a, e);
          const existing = em.get(e);
          rows.push(existing ? {...existing, agentName:name} : {conversationId:null,agentEmail:e,agentName:name,lastMessage:'',lastMessageAt:'',adminUnread:0,agentUnread:0,_noConv:true});
        }
      }
      const seen=new Map();
      for (const r of rows) { if(!seen.has(r.agentEmail)||r.conversationId) seen.set(r.agentEmail,r); }

      // Also include any conversations from non-registered agents / typo emails
      // These would have messages but the agent isn't in the agents list
      for (const conv of existing) {
        const e = (conv.agentEmail||'').toLowerCase();
        if (!seen.has(e)) seen.set(e, conv);
      }

      setConvs(sortConvs([...seen.values()]));
    } catch(e){console.error('[AdminSupport]',e);}
    finally { setListLoading(false); }
  }, [token]);

  useEffect(()=>{loadConvs();},[loadConvs]);

  useEffect(()=>{
    const s=io('/chat',{auth:{token},transports:['websocket','polling']});
    socketRef.current=s;
    s.on('message:new',({message})=>{
      // Play sound for inbound messages (not sent by admin)
      if (message.senderRole !== 'admin') playSound();
      if(message.conversationId===selectedRef.current?.conversationId){
        setMessages(prev=>prev.find(m=>m.messageId===message.messageId)?prev:[...prev,message]);
        scrollDown();
      }
      setConvs(prev=>{
        const idx=prev.findIndex(c=>c.conversationId===message.conversationId);
        if(idx>=0){
          const up={...prev[idx],lastMessage:message.content,lastMessageAt:message.timestamp,adminUnread:message.senderRole==='agent'?(prev[idx].adminUnread||0)+1:prev[idx].adminUnread};
          return sortConvs(prev.map((c,i)=>i===idx?up:c));
        }
        return sortConvs([...prev,{conversationId:message.conversationId,agentEmail:message.senderEmail,agentName:message.senderName||message.senderEmail,lastMessage:message.content,lastMessageAt:message.timestamp,adminUnread:message.senderRole==='agent'?1:0,agentUnread:0}]);
      });
    });
    s.on('message:deleted',({conversationId,messageId})=>{
      if(conversationId!==selectedRef.current?.conversationId)return;
      setMessages(prev=>prev.map(m=>m.messageId===messageId?{...m,deletedAt:new Date().toISOString()}:m));
    });
    s.on('message:reaction',({conversationId,messageId,reactions})=>{
      if(conversationId!==selectedRef.current?.conversationId)return;
      setMessages(prev=>prev.map(m=>m.messageId===messageId?{...m,reactions}:m));
    });
    return()=>s.disconnect();
  },[token]);

  useEffect(()=>{
    if(!socketRef.current||!selected?.conversationId)return;
    socketRef.current.emit('join:conversation',{conversationId:selected.conversationId});
    return()=>socketRef.current?.emit('leave:conversation',{conversationId:selected.conversationId});
  },[selected?.conversationId]);

  useEffect(()=>{
    if(!selected?.conversationId){setMessages([]);return;}
    setLoading(true);
    axios.get(`/api/support/admin/conversations/${selected.conversationId}/messages`,authH(token))
      .then(r=>{setMessages((r.data.messages||[]).slice().reverse());setConvs(prev=>prev.map(c=>c.conversationId===selected.conversationId?{...c,adminUnread:0}:c));scrollDown();})
      .catch(console.error).finally(()=>setLoading(false));
  },[selected?.conversationId]);

  const selectAgent=async(row)=>{
    if(row.conversationId){setSelected(row);return;}
    try{
      const r=await axios.post('/api/support/admin/init-conversation',{agentEmail:row.agentEmail,agentName:row.agentName},authH(token));
      const up={...row,...r.data.conversation,_noConv:false};
      setConvs(prev=>prev.map(c=>c.agentEmail===row.agentEmail?up:c)); setSelected(up);
    }catch(e){console.error(e);setSelected(row);}
  };

  const sendMessage=async()=>{
    if(!input.trim()||!selected||sending)return;
    const content=input.trim(); setInput(''); setSending(true);
    try{
      let convId=selected.conversationId;
      if(!convId){
        const ir=await axios.post('/api/support/admin/init-conversation',{agentEmail:selected.agentEmail,agentName:selected.agentName},authH(token));
        const nc=ir.data.conversation; convId=nc.conversationId;
        const up={...selected,...nc,_noConv:false};
        setSelected(up); selectedRef.current=up;
        setConvs(prev=>prev.map(c=>c.agentEmail===selected.agentEmail?up:c));
        socketRef.current?.emit('join:conversation',{conversationId:convId});
      }
      const res=await axios.post(`/api/support/admin/conversations/${convId}/messages`,{content},authH(token));
      const msg=res.data.message;
      if(msg){setMessages(prev=>prev.find(m=>m.messageId===msg.messageId)?prev:[...prev,msg]);scrollDown();setConvs(prev=>sortConvs(prev.map(c=>c.conversationId===convId?{...c,lastMessage:content,lastMessageAt:msg.timestamp}:c)));}
    }catch(e){console.error(e);setInput(content);}
    finally{setSending(false);}
  };

  const deleteMsg=async(mid)=>{try{await axios.delete(`/api/support/admin/conversations/${selected.conversationId}/messages/${mid}`,authH(token));}catch(e){console.error(e);}};
  const reactMsg=async(mid,emoji)=>{try{await axios.post(`/api/support/admin/conversations/${selected.conversationId}/messages/${mid}/react`,{emoji},authH(token));}catch(e){console.error(e);}};

  const sendFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !selected) return; e.target.value=''; setUploadPct(0);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(
        `/api/support/admin/conversations/${selected.conversationId}/upload`, fd,
        { headers: { ...authH(token).headers }, onUploadProgress: ev => setUploadPct(Math.round(ev.loaded/ev.total*100)) }
      );
      setUploadPct(null);
      if (data.message) { setMessages(prev => prev.find(m=>m.messageId===data.message.messageId)?prev:[...prev,data.message]); scrollDown(); }
    } catch (err) { setUploadPct(null); console.error(err); alert('Upload failed'); }
  };

  const filtered=convs.filter(c=>!search||(c.agentName||c.agentEmail||'').toLowerCase().includes(search.toLowerCase())||(c.agentEmail||'').toLowerCase().includes(search.toLowerCase()));
  const totalUnread=convs.reduce((s,c)=>s+(c.adminUnread||0),0);

  return (
    <div style={{display:'flex',height:'calc(100vh - 80px)',background:'#f3f4f6'}}>
      {/* Left panel */}
      <div style={{width:300,flexShrink:0,background:'#fff',borderRight:'1px solid #e5e7eb',display:'flex',flexDirection:'column'}}>
        <div style={{padding:16,borderBottom:'1px solid #e5e7eb'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <h2 style={{margin:0,fontSize:17,fontWeight:700,color:'#111827'}}>Agent Support</h2>
            {totalUnread>0&&<span style={{background:'#ef4444',color:'#fff',borderRadius:999,padding:'2px 8px',fontSize:12,fontWeight:700}}>{totalUnread}</span>}
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search agents…" style={{width:'100%',border:'1px solid #d1d5db',borderRadius:7,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}/>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {listLoading&&<div style={{padding:32,textAlign:'center',color:'#9ca3af',fontSize:14}}><div style={{fontSize:28,marginBottom:10}}>⏳</div>Loading agents…</div>}
          {!listLoading&&filtered.length===0&&<div style={{padding:32,textAlign:'center',color:'#9ca3af',fontSize:14}}>No agents found</div>}
          {!listLoading&&filtered.map(c=>{
            const isSel=selected?.agentEmail===c.agentEmail;
            return(
              <div key={c.agentEmail} onClick={()=>selectAgent(c)} style={{padding:'12px 14px',cursor:'pointer',background:isSel?'#eff6ff':'#fff',borderBottom:'1px solid #f3f4f6',display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:38,height:38,borderRadius:'50%',background:c._noConv?'#9ca3af':'#1a56db',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:13,flexShrink:0}}>{initials(c.agentName||c.agentEmail)}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontWeight:600,fontSize:14,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.agentName||c.agentEmail}</span>
                    {(c.adminUnread||0)>0&&<span style={{background:'#ef4444',color:'#fff',borderRadius:999,padding:'1px 7px',fontSize:11,fontWeight:700,flexShrink:0,marginLeft:4}}>{c.adminUnread}</span>}
                  </div>
                  <div style={{fontSize:11,color:'#9ca3af',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:1}}>{c.agentEmail}</div>
                  <div style={{fontSize:12,color:c._noConv?'#d1d5db':'#6b7280',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:2,fontStyle:c._noConv?'italic':'normal'}}>
                    {c._noConv?'No messages yet':c.lastMessage||'No messages yet'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Right panel */}
      {!selected?(
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12,color:'#9ca3af'}}>
          <div style={{fontSize:48}}>💬</div>
          <div style={{fontSize:16,fontWeight:600}}>Select an agent to start chatting</div>
          <div style={{fontSize:13}}>All agent support conversations appear on the left.</div>
        </div>
      ):(
        <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>
          <div style={{padding:'14px 20px',background:'#fff',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:36,height:36,borderRadius:'50%',background:'#1a56db',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:13}}>{initials(selected.agentName||selected.agentEmail)}</div>
            <div>
              <div style={{fontWeight:700,fontSize:15,color:'#111827'}}>{selected.agentName||selected.agentEmail}</div>
              <div style={{fontSize:12,color:'#6b7280'}}>{selected.agentEmail}</div>
            </div>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'16px 0',background:'#f9fafb'}}>
            {loading&&<div style={{textAlign:'center',padding:32,color:'#9ca3af'}}>Loading…</div>}
            {!loading&&messages.length===0&&<div style={{textAlign:'center',padding:32,color:'#9ca3af',fontSize:14}}>No messages yet. Say hello! 👋</div>}
            {messages.map(m=><MsgBubble key={m.messageId} msg={m} myEmail={myEmail} onDelete={deleteMsg} onReact={reactMsg}/>)}
            <div ref={bottomRef}/>
          </div>
          {uploadPct !== null && (
            <div style={{padding:'0 16px 6px'}}>
              <div style={{height:3,background:'#e5e7eb',borderRadius:2,overflow:'hidden'}}>
                <div style={{height:'100%',background:'#1a56db',width:`${uploadPct}%`,transition:'width .2s'}}/>
              </div>
            </div>
          )}
          <div style={{padding:'12px 16px',background:'#fff',borderTop:'1px solid #e5e7eb',display:'flex',gap:8,alignItems:'flex-end'}}>
            <input ref={fileRef} type="file" style={{display:'none'}} onChange={sendFile}/>
            <button onClick={()=>fileRef.current?.click()} title="Attach file"
              style={{padding:'10px 12px',background:'#f3f4f6',color:'#374151',border:'1px solid #e5e7eb',borderRadius:10,cursor:'pointer',fontSize:18,flexShrink:0}}>
              📎
            </button>
            <textarea value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}}
              placeholder="Type a message… (Enter to send)" rows={2}
              style={{flex:1,resize:'none',border:'1px solid #d1d5db',borderRadius:10,padding:'10px 14px',fontSize:14,fontFamily:'inherit',outline:'none'}}/>
            <button onClick={sendMessage} disabled={!input.trim()||sending}
              style={{padding:'10px 20px',background:input.trim()?'#1a56db':'#9ca3af',color:'#fff',border:'none',borderRadius:10,cursor:input.trim()?'pointer':'default',fontSize:14,fontWeight:600}}>
              {sending?'…':'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default AdminSupport;
